import { AD_UNITS, PREBID_PRICE_BUCKETS, PREBID_TIMEOUT } from 'mk/ox';
import { Collapse } from 'mk/ox/hbAdUnits';
import hbSizes, { ScreenSizes } from 'mk/ox/hbSizes';
import {
    AD_SLOT_REFRESH,
    AD_SLOT_REGISTER,
    AD_SLOT_UNREGISTER,
    adSlotRefresh,
    AdSlotRefreshAction,
    AdSlotRegisterAction,
    adSlotRenderEnded,
    AdSlotUnregisterAction,
} from 'mk2/containers/AdSlot/AdSlot.actions';
import { getLogger } from 'mk2/logger';
import * as React from 'react';
import { delay, eventChannel } from 'redux-saga';
import { all, call, fork, put, take, takeEvery, takeLatest } from 'redux-saga/effects';

const logger = getLogger('AdSlot.sagas');

let googletag;
let pbjs;
let initialized = false;

declare const window;
declare const sssp;

let slotRenderEndedChannel = null;
function slotRenderEndedChannelSubscribe() {
    if (!slotRenderEndedChannel) {
        slotRenderEndedChannel = new Promise((resolve) => {
            googletag.cmd.push(() => {
                const channel = eventChannel((emitter) => {
                    let subscribed = false;

                    const handler = (event: googletag.events.SlotRenderEndedEvent) => {
                        const slotId = event.slot.getSlotElementId();
                        emitter({ slotId, event: { isEmpty: event.isEmpty, size: event.size } });
                    };

                    googletag.pubads().addEventListener('slotRenderEnded', handler);
                    subscribed = true;
                    return () => {
                        // googletag has no removeEventListener - so we only stop emitting
                        subscribed = false;
                    };
                });

                resolve(channel);
            });
        });
    }

    return slotRenderEndedChannel;
}

function init() {
    if (initialized) {
        // Already initialized - ignore next call
        return;
    }

    initialized = true;

    pbjs = window.pbjs || {};
    pbjs.que = pbjs.que || [];
    window.pbjs = pbjs;

    googletag = window.googletag || {};
    googletag.cmd = googletag.cmd || [];
    window.googletag = googletag;

    googletag.cmd.push(() => {
        const pubads = googletag.pubads();
        pubads.disableInitialLoad();
        pubads.enableSingleRequest();

        // Global collapse setting (can be overridden per slot)
        // Ad slots will stay empty most of the time. So, collapse all
        // divs on the page BEFORE the browser fetches any ads
        // https://support.google.com/dfp_premium/answer/3072674?hl=en
        pubads.collapseEmptyDivs(true);

        googletag.enableServices();
    });

    slotRenderEndedChannelSubscribe();

    pbjs.que.push(() => {
        pbjs.setConfig({
            sizeConfig: hbSizes,
            priceGranularity: PREBID_PRICE_BUCKETS,
            bidderTimeout: PREBID_TIMEOUT,
        });

        // TODO: Move enable analytics to mk/settings as a flag
        // if (enableAnalytics) {
        //     this.pbjs.enableAnalytics({
        //         provider: 'ga',
        //         options: {
        //             global: 'ga', // <string> name of GA global. Default is 'ga'
        //             trackerName: 'pbjsTracker',
        //             enableDistribution: true,
        //         },
        //     });
        // }
    });
}

const registeredSlots = new Map<string, googletag.Slot>();
const registering = new Set<string>();
function* adSlotRegistered({ slotId, zone, targeting }: AdSlotRegisterAction) {
    registering.add(slotId);

    // Allways call init - init is smart to not reinitialize yourself
    init();

    const adUnit = AD_UNITS[zone];

    const registerGoogleTag = new Promise((resolve) => {
        googletag.cmd.push(() => {
            if (!registering.has(slotId)) {
                return;
            }

            // https://support.google.com/dfp_premium/answer/3423562?hl=en
            let gptSizeMappingBuilder: googletag.SizeMappingBuilder = googletag.sizeMapping();
            adUnit.sizeMapping.forEach((sm: ScreenSizes) => {
                const screenSize = [sm.minScreenWidth, 0];
                const slotSizes = sm.sizesSupported;
                gptSizeMappingBuilder = gptSizeMappingBuilder.addSize(screenSize, slotSizes);
            });
            const gptSizeMapping: googletag.SizeMappingArray = gptSizeMappingBuilder.build();

            const gSlot = googletag
                .defineSlot(adUnit.gptid, [], slotId)
                .defineSizeMapping(gptSizeMapping)
                .addService(googletag.pubads());

            if (targeting) {
                Object.keys(targeting).forEach((key: string) => {
                    gSlot.setTargeting(key, targeting[key]);
                });
            }

            if (typeof sssp !== 'undefined' && sssp.displaySeznamAds()) {
                gSlot.setTargeting('seznam', 'true');
            }

            // set per zone collapsing (before fetch/after fetch/never)
            // https://support.google.com/dfp_premium/answer/3072674?hl=en
            if (adUnit.collapse !== undefined) {
                switch (adUnit.collapse) {
                    case Collapse.NEVER:
                        gSlot.setCollapseEmptyDiv(false);
                        break;
                    case Collapse.AFTER_FETCH:
                        gSlot.setCollapseEmptyDiv(true);
                        break;
                    case Collapse.BEFORE_FETCH:
                        gSlot.setCollapseEmptyDiv(true, true);
                        break;
                    default:
                    // pass
                }
            }

            resolve(gSlot);
        });
    });

    const registerPbjs = new Promise((resolve) => {
        pbjs.que.push(() => {
            if (!registering.has(slotId)) {
                return;
            }

            pbjs.addAdUnits([{
                ...adUnit,
                code: slotId,
                // bids: adUnit.bids,
                // sizeMapping: adUnit.sizeMapping,
            }]);
            resolve();
        });
    });

    const [ slot ] = yield call(() => Promise.all([ registerGoogleTag, registerPbjs ]));

    if (registering.delete(slotId)) {
        yield put(adSlotRefresh(slotId));
        registeredSlots.set(slotId, slot);
    }
}

function* adSlotUnregistered({ slotId }: AdSlotUnregisterAction) {
    if (!registeredSlots.has(slotId) // Slot is not registered
        && !registering.has(slotId)) { // Slot is not scheduled for register
        return;
    }

    init();

    const unregisterPbjs = new Promise((resolve) => {
        pbjs.que.push(() => {
            if (registeredSlots.has(slotId)) {
                pbjs.removeAdUnit(slotId);
            }
            resolve();
        });
    });

    const unregisterGoogleTag = new Promise((resolve) => {
        googletag.cmd.push(() => {
            if (registeredSlots.has(slotId)) {
                googletag.destroySlots([registeredSlots.get(slotId)]);
            }
            resolve();
        });
    });

    yield call(() => Promise.all([ unregisterPbjs, unregisterGoogleTag ]));

    registeredSlots.delete(slotId);
}

const slotsToRefresh = new Set<string>();
function* adSlotRefreshed({ slotId }: AdSlotRefreshAction) {
    slotsToRefresh.add(slotId);

    // yield call(() => new Promise((resolve) => requestAnimationFrame(resolve)));
    yield call(delay, 0);

    googletag.cmd.push(() => {
        pbjs.que.push(() => {
            // Now we can refresh ads
            const gSlots = [];
            const slotsIds = [];
            slotsToRefresh.forEach((id: string) => {
                if (registeredSlots.has(id)) {
                    gSlots.push(registeredSlots.get(id));
                    slotsIds.push(id);
                }
            });
            slotsToRefresh.clear();

            // Some slots can be already unregistered
            if (gSlots.length === 0) {
                return;
            }

            let adserverRequestSent = false;

            const callDFP = () => {
                if (adserverRequestSent) {
                    return;
                }
                adserverRequestSent = true;

                // send request to DFP to display the best bid
                pbjs.setTargetingForGPTAsync(slotsIds);

                // DFP compares the best bid with offers from ADX (Google's ad exchange)
                // and displays the priciest ad
                googletag.pubads().refresh(gSlots);
            };

            // call bidders
            pbjs.requestBids({
                adUnitCodes: slotsIds,
                bidsBackHandler: () => {
                    // print best bid to console
                    // var targetingParams = pbjs.getAdserverTargeting();
                    // console.log(JSON.stringify(targetingParams, null, 2));
                    callDFP();
                },
            });

            // if bidders do not complete within PREBID_TIMEOUT,
            // call DFP with the partial result
            setTimeout(() => {
                callDFP();
            }, PREBID_TIMEOUT);
        });
    });
}

function* emitRenderEnded() {
    init();

    const channel = yield call(slotRenderEndedChannelSubscribe);
    try {
        while (true) {
            const { slotId, event } = yield take(channel);
            yield put(adSlotRenderEnded(slotId, event));
        }
    } catch (err) {
        logger.error(err);
    }
}

export default function* root() {
    yield all([
        takeEvery(AD_SLOT_REGISTER, adSlotRegistered),
        takeEvery(AD_SLOT_UNREGISTER, adSlotUnregistered),
        takeLatest(AD_SLOT_REFRESH, adSlotRefreshed),

        process.env.SERVER ? null : fork(emitRenderEnded),
    ]);
}
